import { useContext, useEffect, useState, useMemo, useCallback } from "react"
import PropTypes from 'prop-types';
import { getData } from "src/actions/getData"
import Table from "src/components/Table";
import shopContext from "src/context/shopContext"
import { numberWithCommas } from "src/utils/thousandSeprator";
import moment from 'jalali-moment';
import ProductStatisticsChart from "./ProductStatisticsChart";
import Skeleton from "react-loading-skeleton";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import FilterBox from "src/components/FilterBox";
import { persianToEnglishDigitConverter } from "src/utils/persianToEnglishDigitConverter";
import DatePicker from "react-multi-date-picker";
import { MdAccessTime } from "react-icons/md";
import { RxExternalLink } from "react-icons/rx";
import { TbBasketSearch } from "react-icons/tb";
import Modal from "src/components/Modal";
import ModalFooter from "src/components/Modal/ModalFooter";
import WhiteBoard from "src/components/WhiteBoard";
import ModalBody from "src/components/Modal/ModalBody";
import ModalHeader from "src/components/Modal/ModalHeader";
import TwinButton from "src/components/TwinButton";

// کامپوننت‌های جداگانه
const ProductImage = ({ src }) => (
  <img className="w-[60px] max-w-[60px] rounded-lg" src={src} alt="product" />
);

ProductImage.propTypes = {
  src: PropTypes.string.isRequired
};

const DateRangePicker = ({ onChange, placeholder }) => (
  <DatePicker
    style={{
      width: "100%",
      height: "36px",
      borderRadius: "10px",
      border: "1px solid #e5e7eb",
    }}
    placeholder={placeholder}
    onChange={onChange}
    calendar={persian}
    locale={persian_fa}
    format="YYYY/MM/DD"
    className="rmdp-mobile"
  />
);

DateRangePicker.propTypes = {
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired
};

// ثابت‌ها
const DateRange = {
  START: 0,
  END: 1
};

const TABLE_COLUMNS = [
  {
    id: "image",
    cell: (info) => <ProductImage src={info.getValue()} />,
    header: "تصویر",
  },
  {
    id: "name",
    cell: (info) => info.getValue() || "---",
    header: "نام محصول",
  },
  {
    id: "sku",
    cell: (info) => info.getValue() || "---",
    header: "sku",
  },
  {
    id: "global_unique_id",
    cell: (info) => info.getValue() || "---",
    header: "بارکد",
  },
  {
    id: "categoriesString",
    cell: (info) => info.getValue() || "---",
    header: "دسته بندی",
  },
  {
    id: "brand",
    cell: (info) => info.getValue() || "---",
    header: "برند",
  },
  {
    id: "pa_color",
    cell: (info) => info.getValue() || "---",
    header: "رنگ",
  },
  {
    id: "pa_size",
    cell: (info) => info.getValue() || "---",
    header: "حجم/وزن",
  },
  {
    id: "regular_price",
    cell: (info) => info.getValue() ? numberWithCommas(info.getValue()) + " تومان" : "---",
    header: "قیمت مصرف کننده",
  },
  {
    id: "quantity",
    cell: (info) => info.getValue() || 0,
    header: "تعداد فروش رفته",
  },
  {
    id: "stock_quantity",
    cell: (info) => info.getValue() || "ناموجود",
    header: "موجودی سایت",
  },
  {
    id: "averageCountInBasket",
    cell: (info) => info.getValue() || "-",
    header: "میانگین تعداد در سبد",
  },
  {
    id: "averageSalePrice",
    cell: (info) => info.getValue() ? `${numberWithCommas(info.getValue())} تومان` : "-",
    header: "میانگین قیمت فروش",
  },
  {
    id: "totalSalePrice",
    cell: (info) => info.getValue() ? `${numberWithCommas(info.getValue())} تومان` : "-",
    header: "مجموع فروش",
  },
  {
    id: "coOccurrence",
    cell: (info) => info.getValue() ? JSON.stringify(info.getValue()) : "-",
    header: "محصولاتی که با این محصول به طور همزمان خریداری شده‌اند",
    inTable: false,
  },
  {
    id: "permalink",
    cell: (info) => (
      <div className="flex gap-2">
        <div className="flex gap-2">
          <a
            title="لینک محصول"
            className="cursor-pointer hover:bg-gray-100 hover:text-gray-600 transition-all ease-in-out duration-300 dark:bg-gray-700 bg-gray-50 text-gray-400 p-2 rounded-lg"
            target="_blank"
            rel="noopener noreferrer"
            href={info.getValue()}
          >
            <RxExternalLink className="text-blue-500" />
          </a>
        </div>
        <div className="flex gap-2">
          <span
            title="محصولاتی که با این محصول به طور همزمان خریداری شده‌اند"
            className="cursor-pointer hover:bg-gray-100 hover:text-gray-600 transition-all ease-in-out duration-300 dark:bg-gray-700 bg-gray-50 text-gray-400 p-2 rounded-lg"
            onClick={() => {
              info.row.original.coOccurrence && info.row.original.coOccurrence.length > 0 &&
                info.toggleModal(true, info.row.original);
            }}
          >
            <TbBasketSearch className="text-green-500" />
          </span>
        </div>
      </div>
    ),
    header: "عملیات",
    footer: (info) => info.column.id,
    enableColumnFilter: false,
  },
];

export default function ProductStatistics() {
  const { sales, setSales } = useContext(shopContext);
  const { products, setProducts } = useContext(shopContext);
  const [timePeriod, setTimePeriod] = useState();
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // توابع محاسباتی
  const calculateDateRange = useCallback((date) => {
    return Date.parse(moment
      .from(date ? date : moment().locale('fa').format('YYYY/MM/DD'), "fa", "YYYY/MM/DD")
      .locale("en"));
  }, []);

  const handleDateChange = useCallback((value, index) => {
    setTimePeriod(old => [
      index === DateRange.START ? persianToEnglishDigitConverter(value?.format("YYYY/MM/DD")) : old?.[0],
      index === DateRange.END ? persianToEnglishDigitConverter(value?.format("YYYY/MM/DD")) : old?.[1]
    ]);
  }, []);

  const toggleModal = useCallback((show, product = null) => {
    setShowModal(show);
    setSelectedProduct(product);
  }, []);

  // دریافت داده‌ها
  useEffect(() => {
    const controller = new AbortController(); // برای لغو درخواست
    const signal = controller.signal;

    if (!products) {
      getData(setProducts, "/products", {}, signal);
    }

    return () => {
      controller.abort();
    };
  }, [products]);

  useEffect(() => {
    const controller = new AbortController(); // برای لغو درخواست
    const signal = controller.signal;

    if (!sales) {
      getData(setSales, "/sales", {}, signal);
    }

    return () => {
      controller.abort();
    };
  }, [])

  // محاسبات
  const filteredSales = useMemo(() => {
    if (sales && timePeriod) {
      return sales.filter(sale => {
        const datePaid = Date.parse(sale.date_paid);
        const from = calculateDateRange(timePeriod[0]);
        const to = calculateDateRange(timePeriod[1]);
        return datePaid > from && datePaid <= to;
      });
    }
    return sales;
  }, [sales, timePeriod, calculateDateRange]);

  const productStatistics = useMemo(() => {
    if (filteredSales && products) {
      // گروه‌بندی فروش‌ها بر اساس شماره سفارش
      const orderGroups = filteredSales.reduce((acc, sale) => {
        if (!acc[sale.orderId]) {
          acc[sale.orderId] = [];
        }
        acc[sale.orderId].push(sale);
        return acc;
      }, {});

      // محاسبه محصولات مرتبط و نرخ همبستگی
      const coOccurrenceMap = {};
      const totalOrders = Object.keys(orderGroups).length;

      // محاسبه تعداد کل سفارش‌های هر محصول
      Object.values(orderGroups).forEach(orderItems => {
        orderItems.forEach(item1 => {
          if (!coOccurrenceMap[item1.sku]) {
            coOccurrenceMap[item1.sku] = {
              relatedProducts: {},
              totalOrders: 0
            };
          }
          coOccurrenceMap[item1.sku].totalOrders++;
        });
      });

      // محاسبه تعداد همزمانی محصولات
      Object.values(orderGroups).forEach(orderItems => {
        const uniqueItems = [...new Set(orderItems.map(item => item.sku))];
        uniqueItems.forEach(sku1 => {
          uniqueItems.forEach(sku2 => {
            if (sku1 !== sku2) {
              if (!coOccurrenceMap[sku1].relatedProducts[sku2]) {
                const item2 = orderItems.find(item => item.sku === sku2);
                coOccurrenceMap[sku1].relatedProducts[sku2] = {
                  name: item2.name,
                  count: 0
                };
              }
              coOccurrenceMap[sku1].relatedProducts[sku2].count++;
            }
          });
        });
      });

      let stats = filteredSales.reduce((acc, current) => {
        const existingEntry = acc.find(entry => entry.sku === current.sku);
        if (existingEntry) {
          existingEntry.quantity += current.quantity;
          existingEntry.sales.push(current);
        } else {
          acc.push({
            sku: current.sku,
            name: current.name,
            image: current.image,
            quantity: current.quantity,
            sales: [current]
          });
        }
        return acc;
      }, []);

      stats = stats.map(productStatistic => {
        const productDetails = products.find(product => product.sku === productStatistic.sku);
        const coOccurrence = coOccurrenceMap[productStatistic.sku];

        // تبدیل به آرایه و مرتب‌سازی بر اساس rate به صورت نزولی
        const allRelatedProducts = coOccurrence ? Object.entries(coOccurrence.relatedProducts)
          .map(([sku, data]) => ({
            sku,
            name: data.name,
            rate: parseFloat((data.count / coOccurrence.totalOrders).toFixed(2))
          }))
          .sort((a, b) => b.rate - a.rate) : [];

        // انتخاب 5 محصول اول با بیشترین rate
        const topRelatedProducts = allRelatedProducts.slice(0, 5);

        return {
          ...productStatistic,
          ...productDetails,
          coOccurrence: topRelatedProducts
        };
      });
      stats = stats.map(product => ({
        ...product,
        averageSalePrice: (product.sales.reduce((sum, item) => sum + item.price, 0) / (product.sales.length * 10)).toFixed(0),
        totalSalePrice: (product.sales.reduce((sum, item) => sum + item.price, 0) / 10).toFixed(0),
        averageCountInBasket: (product.sales.reduce((sum, item) => sum + item.quantity, 0) / product.sales.length).toFixed(2),
      }));
      const publishedProducts = products.filter(product => product.status == "publish")
      const merged = [...publishedProducts, ...stats]
      return Array.from(
        new Map(merged.map(item => [item.sku, item])).values()
      );
    }
  }, [filteredSales, products]);

  const filterOptions = useMemo(() => ({
    width: 350,
    title: <span className="flex gap-1 items-center"><MdAccessTime size={20} />بازه زمانی:</span>,
    value: `از ${timePeriod?.[0] || "ابتدا"} تا ${timePeriod?.[1] || "کنون"}`
  }), [timePeriod]);

  const columns = useMemo(() => TABLE_COLUMNS.map(col => {
    if (col.id === "permalink") {
      return {
        ...col,
        cell: (info) => col.cell({ ...info, toggleModal })
      };
    }
    return col;
  }), [toggleModal]);

  return (
    <>
      <WhiteBoard>
        <div className="flex gap-2 flex-col md:flex-row justify-between items-center">
          {sales && products ? (
            <div className="text-lg">
              <span className="text-gray-400">امروز،</span>
              <span className="mx-1">{moment().locale('fa').format('dddd YYYY/MM/DD')}</span>
            </div>
          ) : (
            <Skeleton width={200} height={40} className="!rounded-xl" />
          )}
          {sales && products ? (
            <FilterBox {...filterOptions}>
              <div className="flex flex-col gap-2">
                <DateRangePicker
                  onChange={(value) => handleDateChange(value, DateRange.START)}
                  placeholder="از تاریخ ..."
                />
                <DateRangePicker
                  onChange={(value) => handleDateChange(value, DateRange.END)}
                  placeholder="تا تاریخ ..."
                />
              </div>
            </FilterBox>
          ) : (
            <Skeleton width={350} height={40} className="!rounded-xl" />
          )}
        </div>
      </WhiteBoard>
      <Table data={productStatistics} columns={columns} CSVExportPermission="exportProductsStatistics" tableName="productStatistics" />

      <Modal
        isOpen={showModal}
        onClose={() => toggleModal(false)}
        title={`محصولات مرتبط با ${selectedProduct?.name}`}
      >
        <ModalHeader>
          <span className="text-base font-medium">محصولاتی که با {selectedProduct?.name} به طور همزمان خریداری شده‌اند</span>
        </ModalHeader>
        <ModalBody>
          <div className="flex flex-col gap-2 p-4 text-sm">
            {selectedProduct?.coOccurrence.map((product, index) => (
              <div key={product.sku} className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                <div className="flex gap-2 items-center">
                  <ProductImage src={products.find(p => p.sku === product.sku)?.image} />
                  <span>{index + 1}. {product.name}</span>
                  <span>({product.sku})</span>
                </div>
                <span className="text-gray-500">نرخ همبستگی: {parseInt(product.rate * 100)}%</span>
              </div>
            ))}
          </div>
        </ModalBody>
        <ModalFooter>
          <TwinButton
            rejectHandle={() => toggleModal(false)}
            acceptHandle={() => toggleModal(false)}
            acceptTitle={false}
            rejectTitle="بستن"
            isCompact={true}
          />
        </ModalFooter>
      </Modal>
    </>
  );
}
